import React, { useState, useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import DropdownInputMC from './DropdownInputMC';

/**
 * TemplateEditorMC component
 *
 * @param {string} template - **Required** - The template string to be rendered. Use '*' to indicate a variable.
 * @param {Array<IOptionProps>} options - **Required** - Array of option objects to populate the dropdown.
 * @param {string} name - **Required** - The name of the field to be used in the form.
 *
 * @typedef {Object} IOptionProps
 * @property {string} name - **Required** - Text to display in the dropdown list for this option.
 * @property {string} value - **Required** - Value to insert in the input when the option is selected.
 */
const TemplateEditorMC = ({ template, options, name }) => {
  const [paramsTemplate, setParamsTemplate] = useState({});
  const containerRef = useRef(null);
  const {
    control, formState: { errors }, trigger, getValues
  } = useFormContext();

  useEffect(() => {
    const initialParams = {};
    const initialValues = getValues();
    template.split('').forEach((char, index) => {
      if (char === '*' && (index === 0 || template[index - 1] !== '\\')) {
        const initialValue = initialValues && initialValues[name] ? initialValues[name][index] : '';
        initialParams[index] = initialValue;
      }
    });
    setParamsTemplate(initialParams);
  }, [template]);

  const selectOption = (index, option, field) => {
    const updatedParamsTemplate = { ...paramsTemplate, [index]: option };
    setParamsTemplate(updatedParamsTemplate);
    field.onChange(updatedParamsTemplate);

    if (errors[name]) {
      trigger(name);
    }
  };

  const renderTemplate = (field) => {
    const array = template.split('');
    const contentBlocks = [];

    array.forEach((char, index) => {
      if (Object.prototype.hasOwnProperty.call(paramsTemplate, index)) {
        const key = `autocomplete-${index}`;
        const fieldName = `${name}[${index}]`;
        contentBlocks.push(
          <DropdownInputMC
            key={key}
            id={index}
            options={options}
            onSelect={(option) => selectOption(index, option, field)}
            name={fieldName}
            defaultValue={paramsTemplate[index]}
            fullWidth
          />
        );
        return;
      }
      if (char === '\\') {
        if (index === array.length - 1 || array[index + 1] !== '*') {
          const key = `span-${index}`;
          contentBlocks.push(
            <span key={key}>
              {char}
            </span>
          );
          return;
        }
        return;
      }
      const lastBlock = contentBlocks[contentBlocks.length - 1];
      if (lastBlock && typeof lastBlock === 'string') {
        contentBlocks[contentBlocks.length - 1] += char;
      } else {
        contentBlocks.push(char);
      }
    });
    return contentBlocks.map((block) => {
      if (typeof block === 'string') {
        return (
          <span key={`text-${block}`}>
            {block}
          </span>
        );
      }
      return block;
    });
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, formState }) => (
        <div ref={containerRef} className="break-words tw-text-sm">
          {renderTemplate(field)}
          {formState.errors[name] && (
            <p className="tw-text-xs tw-mt-4 tw-text-red-500">{formState.errors[name]?.message}</p>
          )}
        </div>
      )}
    />
  );
};

export default TemplateEditorMC;
